import type { DataProvider } from "./types/index";
import { invalidateCache } from "./cacheInstance";

let _provider: DataProvider;

/**
 * Map of mutation method name → cache keys to invalidate after the call
 * succeeds. Audit C6 — without this, the next refreshData() returns stale
 * data from the cache for up to `refreshIntervalMs` after the user makes
 * a change. Mutations bypass the cache entirely; reads are still cached.
 */
const MUTATION_INVALIDATIONS: Record<string, string[]> = {
  upsertForecast: ["forecasts"],
  upsertRule: ["rules"],
  deleteRule: ["rules"],
  createMaster: ["assets"],
  createChildAsset: ["assets"],
  // sync touches both sides — assets get a status flip, forecasts get appended
  syncValidatedToForecasts: ["assets", "forecasts"],
  // audit C7 — Data Access Rules (T9)
  upsertDataAccessRule: ["dataAccessRules"],
  deleteDataAccessRule: ["dataAccessRules"],
};

/**
 * Wrap a provider in a Proxy that auto-invalidates the matching cache
 * keys after each mutation. Reads pass through untouched. Failed
 * mutations (rejected promises) do NOT trigger invalidation.
 */
function withCacheInvalidation(provider: DataProvider): DataProvider {
  return new Proxy(provider, {
    get(target, prop, receiver) {
      const value = Reflect.get(target, prop, receiver);
      if (typeof value !== "function") return value;
      const keys = MUTATION_INVALIDATIONS[prop as string];
      if (!keys) return value.bind(target);
      return async function (...args: any[]) {
        const result = await value.apply(target, args);
        for (const k of keys) invalidateCache(k);
        return result;
      };
    },
  });
}

export function setProvider(p: DataProvider): void {
  _provider = withCacheInvalidation(p);
}

export function getProvider(): DataProvider {
  if (!_provider) throw new Error("Provider not initialized. Call setProvider() first.");
  return _provider;
}
